/**
 * 🎯 Skills Component - Direct API Integration
 * Version migrated to use direct API calls instead of skillsSyncService
 */
import React, { useState, useEffect, useMemo } from 'react';
import { vars } from '../../../../styles/vars.css';
import { useInView } from 'react-intersection-observer';
import * as styles from './skills.css';
import { commonClasses } from "../../../../styles/indexStyles";
import { useLanguage } from "../../../../hooks";
import { portfolioApi } from '../../../shared/services/portfolioApi';
import type { SkillItem } from '../../../../types';
import { TechIcon } from '../../../shared/components/TechIcon/TechIcon';
import { Spinner } from '../../../admin/components/Spinner/Spinner';
import { SkillSphere } from './SkillSphere';
import { getSphereSettings, type SphereSettings } from '../../../shared/services/featureFlags';
import { FaStar } from 'react-icons/fa';

// Category icon imports
import { MdWeb, MdStorage, MdBuild, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { FaCode, FaServer, FaTools, FaChartBar, FaLightbulb } from 'react-icons/fa';

// Translation imports for component accessibility
// import { useTranslation } from 'react-i18next';

// Couleur de halo par famille (utilisée par la sphère si l'option est activée)
const CATEGORY_HALO: Record<string, string> = {
  'frontend': '#61dafb',
  'backend': '#339933',
  'ia / data': '#8e44ad',
  'ia/data': '#8e44ad',
  'data science': '#8e44ad',
  'data': '#8e44ad',
  'base de données': '#27ae60',
  'database': '#27ae60',
  'devops': '#e67e22',
};

export const Skills: React.FC = () => {  // ==================== HOOKS ====================
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { language } = useLanguage();
  // const { t } = useTranslation();

  // ==================== STATE ====================
  const [skills, setSkills] = useState<SkillItem[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [sphereSettings, setSphereSettings] = useState<SphereSettings>({
    rotationSpeed: 1, size: 460, iconSize: 28, showNames: true, categoryHalo: false,
  });

  // ==================== EFFECTS ====================
  useEffect(() => {
    const loadSkillsFromAPI = async () => {
      try {
        console.log('🚀 Skills: Starting direct API call...');
        setLoadingSkills(true);
        setSkillsError(null);
        
        const response = await portfolioApi.skills.getAll();
        console.log('📊 Skills: API Response:', response);
        
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erreur lors du chargement des compétences');
        }

        const apiSkills: SkillItem[] = response.data;
        console.log('📋 Skills: Received skills:', apiSkills.length, 'items');
        
        // Only visible skills, sorted by order - exactement comme dans l'admin
        const visibleSkills = apiSkills
          .filter(skill => skill.is_visible !== false)
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        
        console.log('🔍 Skills: Visible skills after filtering:', visibleSkills.length);
        console.log('✅ Skills: Skills loaded successfully');

        setSkills(visibleSkills);
        
      } catch (err: any) {
        console.error('❌ Skills: Error loading from API:', err);
        setSkillsError(err.message);
        setSkills([]);
      } finally {
        setLoadingSkills(false);
      }
    };

    loadSkillsFromAPI();
  }, [language]);

  useEffect(() => {
    if (inView && !animate) {
      const timer = setTimeout(() => setAnimate(true), 200);
      return () => clearTimeout(timer);
    }
  }, [inView, animate]);

  // Charge les réglages de la sphère (et réagit aux mises à jour depuis le dashboard admin)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const s = await getSphereSettings();
        if (mounted) setSphereSettings(s);
      } catch { /* valeurs par défaut conservées */ }
    };
    load();
    const onFlagsUpdated = async () => {
      try {
        const { clearFlagsCache } = await import('../../../shared/services/featureFlags');
        clearFlagsCache();
      } catch { /* noop */ }
      load();
    };
    window.addEventListener('featureFlagsUpdated', onFlagsUpdated as EventListener);
    return () => {
      mounted = false;
      window.removeEventListener('featureFlagsUpdated', onFlagsUpdated as EventListener);
    };
  }, []);

  // ==================== HELPERS ====================
  const favoriteSkills = useMemo(() => skills.filter(skill => skill.is_favorite), [skills]);
  // Compétences enrichies d'une couleur de halo par famille pour la sphère
  const sphereSkills = useMemo(
    () => skills.map(s => ({
      ...s,
      haloColor: CATEGORY_HALO[(s.category_fr || '').toLowerCase()] || '#64ffda',
    })),
    [skills]
  );
  
  /**
   * 🎨 Category Icon Resolver
   */
  const getCategoryIcon = (category: string): React.ReactNode => {
    const iconProps = { size: 20, className: styles.categoryIcon };
    
    switch (category.toLowerCase()) {
      case 'frontend':
        return <MdWeb {...iconProps} color="#61dafb" />;
      case 'backend':
        return <FaServer {...iconProps} color="#339933" />;
      case 'tools':
      case 'outils':
        return <FaTools {...iconProps} color="#f39c12" />;
      case 'data science':
      case 'ia / data':
      case 'ia/data':
      case 'data':
        return <FaChartBar {...iconProps} color="#8e44ad" />;
      case 'database':
      case 'base de données':
        return <MdStorage {...iconProps} color="#27ae60" />;
      case 'notions':
      case 'concepts':
        return <FaLightbulb {...iconProps} color="#e74c3c" />;
      case 'devops':
        return <MdBuild {...iconProps} color="#e67e22" />;
      default:
        return <FaCode {...iconProps} color="#2c3e50" />;
    }
  };

  /**
   * 🛠️ Technology Icon Resolver
   */
  const getSkillIcon = (iconType?: string, size: number = 24, color?: string): React.ReactNode => {
    if (!iconType) return <TechIcon type="code" size={size} color={color} />;
    return <TechIcon type={iconType} size={size} color={color} />;
  };

  // Grouper les compétences par catégorie.
  // IMPORTANT : on affiche TOUTES les catégories ayant des compétences visibles
  // (pas de liste blanche codée en dur) pour rester cohérent avec le dashboard admin.
  const skillsByCategory = useMemo(() => {
    const groups: Record<string, SkillItem[]> = {};

    skills.forEach(skill => {
      const category = skill.category_fr || 'Autres';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(skill);
    });

    // Ordre préféré : la famille IA est mise en avant (en premier), puis le reste.
    const preferredOrder = [
      'IA / Data', 'Data Science',
      'Backend', 'Frontend',
      'Base de données', 'Database',
      'DevOps', 'Outils', 'Notions'
    ];

    const minOrder = (cat: string) =>
      Math.min(...groups[cat].map(s => s.order_index ?? 0));

    return Object.keys(groups)
      .filter(cat => groups[cat] && groups[cat].length > 0)
      .sort((a, b) => {
        const ia = preferredOrder.indexOf(a);
        const ib = preferredOrder.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return minOrder(a) - minOrder(b);
      })
      .map(cat => ({ category: cat, skills: groups[cat] }));
  }, [skills]);

  // ==================== RENDER CONDITIONS ====================
  if (loadingSkills) {
    return (
      <section id="skills" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={commonClasses.highlight}>02.</span> 
            {language === 'fr' ? 'Mes Compétences' : 'My Skills'}
          </h2>
          <div className={styles.loading}>
            <Spinner size="large" ariaLabel="Chargement des compétences" />
          </div>
        </div>
      </section>
    );
  }

  const hasAnySkills = skills.length > 0;

  if (!hasAnySkills) {
    return (
      <section id="skills" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={commonClasses.highlight}>02.</span> 
            {language === 'fr' ? 'Mes Compétences' : 'My Skills'}
          </h2>
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#8892b0',
            backgroundColor: 'rgba(100, 255, 218, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(100, 255, 218, 0.1)'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#ccd6f6' }}>
              {language === 'fr' ? '💡 Compétences Bientôt Disponibles' : '💡 Skills Coming Soon'}
            </h3>
            <p style={{ fontSize: '1rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              {language === 'fr' 
                ? 'Aucune compétence visible trouvée dans la base de données.' 
                : 'No visible skills found in the database.'
              }
            </p>
            {skillsError && (
              <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: vars.color.dangerText }}>
                Erreur API : {skillsError}
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <section 
      id="skills" 
      className={`${commonClasses.section} ${styles.section}`}
      ref={ref}
      aria-labelledby="skills-heading"
    >
      <div className={styles.container}>
        <h2 id="skills-heading" className={`${commonClasses.title} ${styles.title}`}>
          <span className={commonClasses.highlight}>02.</span> 
          {language === 'fr' ? 'Mes Compétences' : 'My Skills'}
        </h2>

        <SkillSphere
          skills={sphereSkills}
          language={language}
          rotationSpeed={sphereSettings.rotationSpeed}
          size={sphereSettings.size}
          iconSize={sphereSettings.iconSize}
          showNames={sphereSettings.showNames}
          categoryHalo={sphereSettings.categoryHalo}
        />

        <div className={styles.detailToggleWrap}>
          <button
            type="button"
            className={styles.detailToggle}
            onClick={() => setShowDetails(v => !v)}
            aria-expanded={showDetails}
          >
            {showDetails
              ? (<>{language === 'fr' ? 'Masquer le détail' : 'Hide details'} <MdExpandLess /></>)
              : (<>{language === 'fr' ? 'Voir le détail par famille' : 'View details by category'} <MdExpandMore /></>)}
          </button>
        </div>

        {showDetails && (
        <div className={styles.skillsGrid}>
          {skillsByCategory.map(({ category, skills: categorySkills }, categoryIndex) => {
            const isFeatured = ['ia / data', 'ia/data', 'data science', 'data'].includes(category.toLowerCase());
            return (
            <div 
              key={category} 
              className={`${styles.skillGroup} ${isFeatured ? styles.skillGroupFeatured : ''}`}
              style={{ animationDelay: `${categoryIndex * 0.1}s` }}
            >
              <h3 className={styles.categoryTitle}>
                {getCategoryIcon(category)}
                {category}
              </h3>
              <ul className={`${styles.skillItems} ${isFeatured ? styles.skillItemsFeatured : ''}`}>
                {categorySkills.map(skill => (
                  <li key={skill.id} className={styles.skillItem}>
                    <div className={styles.skillHeader}>
                      <div className={styles.iconWrapper}>
                        {getSkillIcon(skill.image_url || skill.icon_type, 24, skill.color)}
                      </div>
                      <span className={styles.skillName}>{skill.name_fr}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            );
          })}
        </div>
        )}

  {favoriteSkills.length > 0 && (
          <div className={styles.favoriteSkills}>
            <h3 className={styles.favoriteTitle}>
              <FaStar style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
              {language === 'fr' ? 'Mes Outils de Prédilection' : 'My Favorite Tools'}
            </h3>
            <div className={styles.favoriteItems}>
              {favoriteSkills.map(skill => (
                <div key={skill.id} className={styles.favoriteItem}>
                  <div className={styles.favoriteIcon} style={{ display: 'flex', alignItems: 'center' }}>
                    {getSkillIcon(skill.image_url || skill.icon_type, 24, skill.color)}
                  </div>
                  <span className={styles.favoriteName}>
                    {skill.name_fr}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default Skills;
